
import { Component, Mixins } from "vue-property-decorator";
import { AxiosInstance } from "axios";

import {
  Gipt,
  GiptFormResponse,
  GiptPackagesResponse
} from "@/interfaces/meeting/details";
import { setClientAuthentication } from "@/services/api/client";
import {
  loadMeetingState,
  updateGiptForm,
  updateGiptPackages
} from "@/services/api/meeting";
import { JsonConsoleLogger } from "@/services/logging/consoleLogger";
import {
  sendGiptFormChanged,
  sendGiptPackagesChanged
} from "@/services/meeting/messaging";
import ErrorMixin from "@/mixins/ErrorMixin.vue";
import SnackbarMixin from "@/mixins/SnackbarMixin.vue";
import { PackageListResponse } from "@/interfaces/packages/sales_packages";
import { importByEvent } from "@/importing/byEvent";
import { GiptVsWorksheet } from "@/gipt/interface";

@Component
export default class GiptMixin extends Mixins<ErrorMixin, SnackbarMixin>(
  ErrorMixin,
  SnackbarMixin
) {
  apiClient!: AxiosInstance;
  isShowGipt = false;
  isGiptSubmitting = false;
  gipt: Gipt = {};
  giptPackages: PackageListResponse[] = [];

  async created() {
    const token = new URLSearchParams(window.location.search).get("token")!;
    this.apiClient = setClientAuthentication(token);
  }

  async sendGiptForm(
    gipt: Gipt,
    logger: JsonConsoleLogger,
    isUpdatePackages = false
  ) {
    this.isGiptSubmitting = true;
    try {
      await updateGiptForm(this.apiClient, gipt);
      this.gipt = gipt;
      this.isGiptSubmitting = false;
      // Send socket event
      sendGiptFormChanged();
      if (isUpdatePackages) {
        this.sendUpdateGiptPackages(logger);
      }
    } catch (ex) {
      this.setErrorMessages(
        "Unexpected error",
        "An error occured while updating GIPT form, please try again or contact an administrator if error persist."
      );
      this.isDisplayErrorDialog = true;
      this.isGiptSubmitting = false;
    }
  }

  async sendUpdateGiptPackages(logger: JsonConsoleLogger, serverDetails?: any) {
    this.isGiptSubmitting = true;
    let onComplete = (giptPackages: PackageListResponse[]) => {
      let message = "Saved";

      if (giptPackages !== null && serverDetails?.event_id !== 178) {
        this.giptPackages = giptPackages;
        message = `There are ${giptPackages.length} matched packages.`;
      }

      this.showSnackbar(message);
    }
    try {
      const fn = await importByEvent.bind(this)('gipt') as GiptVsWorksheet
      const {onGiptSave} = await fn()
      onComplete = onGiptSave.bind(this)
    } catch(e) {
      console.log('[GIPT] GiptVsWorksheet not implemented')
    }
    try {
      const { giptPackages } = await updateGiptPackages(
        this.apiClient,
        this.gipt
      );
      logger.info("[GIPT] Gipt packages updated: ", giptPackages);
      onComplete(giptPackages)
      this.isGiptSubmitting = false;
      // Send socket event
      sendGiptPackagesChanged();
    } catch (ex) {
      this.setErrorMessages(
        "Unexpected error",
        "An error occured while updating GIPT packages, please try again or contact an administrator if error persist."
      );
      this.isDisplayErrorDialog = true;
      this.isGiptSubmitting = false;
    }
  }

  async loadGiptForm(logger: JsonConsoleLogger) {
    try {
      const { gipt } = await loadMeetingState<GiptFormResponse>(
        this.apiClient,
        "gipt"
      );
      logger.info("[GIPT FORM] GiptForm loaded from state", gipt);
      this.gipt = gipt;
    } catch (ex) {
      this.setErrorMessages(
        "Unexpected error",
        "An error occured while loading GIPT form, please try again or contact an administrator if error persist."
      );
      this.isDisplayErrorDialog = true;
    }
  }

  async loadGiptPackages(logger: JsonConsoleLogger) {
    try {
      const { giptPackages } = await loadMeetingState<GiptPackagesResponse>(
        this.apiClient,
        "giptPackages"
      );
      logger.info(
        "[GIPT PACKAGES] Gipt packages loaded from state",
        giptPackages
      );
      if (giptPackages !== null) {
        this.giptPackages = giptPackages;
        this.showSnackbar(`There are ${giptPackages.length} matched packages.`);
      }
    } catch (ex) {
      this.setErrorMessages(
        "Unexpected error",
        "An error occured while loading GIPT packages, please try again or contact an administrator if error persist."
      );
      this.isDisplayErrorDialog = true;
    }
  }

  async loadGiptFormAndPackages(logger: JsonConsoleLogger) {
    try {
      const { gipt, giptPackages } = await loadMeetingState(this.apiClient, [
        "gipt",
        "giptPackages"
      ]);
      logger.info("[GIPT FORM] GiptForm loaded from state", gipt);
      this.gipt = gipt;
      logger.info(
        "[GIPT PACKAGES] Gipt packages loaded from state",
        giptPackages
      );
      if (giptPackages !== null) {
        this.giptPackages = giptPackages;
      }
    } catch (ex) {
      this.setErrorMessages(
        "Unexpected error",
        "An error occured while loading GIPT form & packages, please try again or contact an administrator if error persist."
      );
      this.isDisplayErrorDialog = true;
    }
  }
}
